/**
 * Explore Quests screen — full-page overlay listing every quest
 * in the comprehensive map, grouped by category.
 *
 * Layout (matches mockup 2):
 *
 *  - Top bar: ← back, "Explore quests" / domain subtitle,
 *    filter icon.
 *  - View switcher (List / Map).
 *  - List view: categories rendered as sections, quests listed
 *    inside each with title, description, "Builds toward:" line,
 *    concept chips, and a tier-status tag (Recommended / Good
 *    next / Later / Research-flavored).
 *  - Map view: procedural SVG map (see ``QuestMap.tsx``).
 *  - "See full domain map & landmarks" bottom link → switches to
 *    the map view.
 */

import React, { useMemo, useState } from "react";
import { css } from "@emotion/css";
import type {
  ArcQuestRecommendation,
  ArcQuestCategory,
  ArcMilestone,
  QuestRecommendationTier,
} from "../../types/arcGenerationSession";
import { questIconSvg } from "../../data/questIcons";
import { QuestMap, CapabilityDetail } from "./QuestMap";

// ── Tier visuals ────────────────────────────────────────────────────

const TIER_TAG: Record<
  QuestRecommendationTier,
  { label: string; bg: string; text: string }
> = {
  best_fit: {
    label: "Recommended",
    bg: "var(--color-primary-muted)",
    text: "var(--color-primary)",
  },
  alternative: {
    label: "Recommended",
    bg: "var(--color-primary-muted)",
    text: "var(--color-primary)",
  },
  good_next: {
    label: "Good next",
    bg: "var(--color-bg-raised)",
    text: "var(--color-text-secondary)",
  },
  later: {
    label: "Later",
    bg: "var(--color-bg-raised)",
    text: "var(--color-text-muted)",
  },
  research_flavored: {
    label: "Research-flavored",
    bg: "#3d2e00",
    text: "#eab308",
  },
};

// ── Styles ──────────────────────────────────────────────────────────

const overlayStyle = css`
  position: fixed;
  inset: 0;
  background: var(--color-bg);
  z-index: 1000;
  display: flex;
  flex-direction: column;
`;

const headerStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px 12px;
  border-bottom: 1px solid var(--color-border-subtle);
`;

const headerBackBtnStyle = css`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: var(--color-text);
  font-size: 1.3em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const headerTitleStyle = css`
  flex: 1;
  text-align: center;
  & > .title {
    font-size: 1.05em;
    font-weight: 700;
    color: var(--color-text);
  }
  & > .subtitle {
    font-size: 0.82em;
    color: var(--color-primary);
    margin-top: 2px;
  }
`;

const viewSwitcherStyle = css`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px 4px;
`;

const viewSwitcherBtnStyle = css`
  flex: 1;
  padding: 9px 12px;
  border-radius: 10px;
  border: 1px solid var(--color-border);
  background: transparent;
  color: var(--color-text-secondary);
  font-size: 0.88em;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
`;

const viewSwitcherBtnActiveStyle = css`
  background: var(--color-primary-muted);
  color: var(--color-primary);
  border-color: var(--color-primary);
`;

const scrollAreaStyle = css`
  flex: 1;
  overflow-y: auto;
  padding: 12px 18px 24px;
`;

const categoryWrapStyle = css`
  margin-top: 16px;
`;

const categoryHeaderStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border-radius: 10px;
  margin-bottom: 8px;
`;

const categoryHeaderTitleStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95em;
  font-weight: 700;
`;

const categoryHeaderCountStyle = css`
  font-size: 0.82em;
  color: var(--color-text-muted);
`;

const questCardStyle = css`
  display: grid;
  grid-template-columns: 72px 1fr;
  gap: 12px;
  padding: 12px;
  border-radius: 12px;
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border);
  margin-bottom: 8px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, transform 0.05s;
  &:hover { border-color: var(--color-primary); background: var(--color-bg-raised); }
  &:active { transform: translateY(1px); }
`;

const questIconStyle = css`
  width: 72px;
  height: 72px;
  border-radius: 10px;
  background: var(--color-primary-muted);
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  & > svg { width: 100%; height: 100%; display: block; }
`;

const questTitleStyle = css`
  font-size: 0.95em;
  font-weight: 700;
  color: var(--color-text);
  line-height: 1.25;
`;

const questDescStyle = css`
  font-size: 0.83em;
  color: var(--color-text-secondary);
  margin-top: 4px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const buildsTowardLineStyle = css`
  font-size: 0.76em;
  color: var(--color-text-muted);
  margin-top: 6px;
  line-height: 1.4;
  & > .label {
    font-weight: 700;
    margin-right: 4px;
  }
`;

const chipRowStyle = css`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  align-items: center;
`;

const chipStyle = css`
  font-size: 0.74em;
  padding: 3px 9px;
  border-radius: 999px;
  background: var(--color-bg-raised);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border-subtle);
`;

const tierTagStyle = css`
  font-size: 0.72em;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  margin-left: auto;
`;

const fullMapLinkStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 14px;
  margin-top: 12px;
  border: 1px solid var(--color-primary);
  border-radius: 12px;
  background: transparent;
  color: var(--color-primary);
  font-size: 0.95em;
  font-weight: 600;
  cursor: pointer;
  &:hover { background: var(--color-primary-muted); }
`;

// ── Helpers ─────────────────────────────────────────────────────────

function parseChips(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function categoryAccentColor(accent: string): string {
  switch (accent) {
    case "primary": return "var(--color-primary)";
    case "success": return "var(--color-success-text)";
    case "warning": return "#eab308";
    case "accent": return "var(--color-loader-rendering)";
    case "neutral":
    default: return "var(--color-text-secondary)";
  }
}

function tierDisplay(tier: QuestRecommendationTier): {
  label: string; bg: string; text: string;
} {
  return TIER_TAG[tier] ?? TIER_TAG.later;
}

// ── Component ───────────────────────────────────────────────────────

interface ExploreQuestsProps {
  quests: ArcQuestRecommendation[];
  categories: ArcQuestCategory[];
  milestones: ArcMilestone[];
  domainName: string;
  onClose: () => void;
  onChooseQuest: (questId: string) => void;
  isStreaming: boolean;
}

export const ExploreQuests: React.FC<ExploreQuestsProps> = ({
  quests,
  categories,
  milestones,
  domainName,
  onClose,
  onChooseQuest,
  isStreaming,
}) => {
  // The map is the more interesting default — the list is one tap
  // away if the learner prefers a flat enumeration.
  const [view, setView] = useState<"list" | "map">("map");
  // Drill-in state lives here (not in QuestMap) so a quest tapped
  // from the LIST and a station tapped on the MAP both open the
  // same capability-detail screen — an intermediary that shows
  // more info and a Start CTA, instead of activating immediately.
  const [drillQuestId, setDrillQuestId] = useState<string | null>(null);

  const milestonesByQuest = useMemo(() => {
    const out: Record<string, ArcMilestone[]> = {};
    for (const m of milestones) (out[m.questId] ??= []).push(m);
    for (const list of Object.values(out)) {
      list.sort((a, b) => Number(a.order || 0) - Number(b.order || 0));
    }
    return out;
  }, [milestones]);

  const drillQuest = drillQuestId
    ? quests.find((q) => q.questId === drillQuestId) ?? null
    : null;

  return (
    <div className={overlayStyle} data-testid="explore-quests">
      <div className={headerStyle}>
        <button
          type="button"
          className={headerBackBtnStyle}
          onClick={onClose}
          aria-label="Back"
          data-testid="explore-quests-back"
        >
          ←
        </button>
        <div className={headerTitleStyle}>
          <div className="title">Explore quests</div>
          {domainName && <div className="subtitle">{domainName}</div>}
        </div>
        {/* Spacer balances the back button so the title stays
            centered (the old ☰ filter button was removed). */}
        <div style={{ width: 32, flexShrink: 0 }} aria-hidden="true" />
      </div>

      {/* View switcher is hidden while drilled into a capability.
          Map is the primary option (left, active by default); the
          list is the secondary alternative. */}
      {!drillQuest && (
        <div className={viewSwitcherStyle}>
          <button
            type="button"
            className={`${viewSwitcherBtnStyle} ${view === "map" ? viewSwitcherBtnActiveStyle : ""}`}
            onClick={() => setView("map")}
            data-testid="explore-quests-view-map"
          >
            Map
          </button>
          <button
            type="button"
            className={`${viewSwitcherBtnStyle} ${view === "list" ? viewSwitcherBtnActiveStyle : ""}`}
            onClick={() => setView("list")}
            data-testid="explore-quests-view-list"
          >
            List
          </button>
        </div>
      )}

      <div className={scrollAreaStyle}>
        {drillQuest ? (
          <CapabilityDetail
            quest={drillQuest}
            milestones={milestonesByQuest[drillQuest.questId] ?? []}
            allQuests={quests}
            accent={categoryAccentColor(
              categories.find((c) => c.categoryId === drillQuest.categoryId)
                ?.accentColor ?? "neutral",
            )}
            onBack={() => setDrillQuestId(null)}
            onChoose={() => onChooseQuest(drillQuest.questId)}
            onJumpToQuest={(qid) => setDrillQuestId(qid)}
            isStreaming={isStreaming}
          />
        ) : view === "list" ? (
          <ListView
            quests={quests}
            categories={categories}
            milestones={milestones}
            onDrillIn={setDrillQuestId}
            switchToMap={() => setView("map")}
          />
        ) : (
          <QuestMap
            quests={quests}
            categories={categories}
            domainName={domainName}
            onDrillIn={setDrillQuestId}
          />
        )}
      </div>
    </div>
  );
};

// ── List view ───────────────────────────────────────────────────────

const ListView: React.FC<{
  quests: ArcQuestRecommendation[];
  categories: ArcQuestCategory[];
  milestones: ArcMilestone[];
  // Tapping a quest opens its capability-detail screen — it does
  // NOT activate the quest.  Activation happens from the Start CTA
  // inside that detail screen.
  onDrillIn: (questId: string) => void;
  switchToMap: () => void;
}> = ({ quests, categories, milestones, onDrillIn, switchToMap }) => {
  // Group quests by category.
  const questsByCategory: Record<string, ArcQuestRecommendation[]> = {};
  for (const q of quests) {
    (questsByCategory[q.categoryId] ??= []).push(q);
  }

  // First section: "Best fit for your goal" — synthesized from the
  // best_fit quest regardless of its category.
  const best = quests.find((q) => q.recommendationTier === "best_fit");

  const milestoneCountByQuest: Record<string, number> = {};
  for (const m of milestones) {
    milestoneCountByQuest[m.questId] = (milestoneCountByQuest[m.questId] ?? 0) + 1;
  }

  return (
    <>
      {best && (
        <div className={categoryWrapStyle}>
          <div
            className={categoryHeaderStyle}
            style={{
              background: "var(--color-primary-muted)",
              color: "var(--color-primary)",
            }}
          >
            <div className={categoryHeaderTitleStyle}>
              <span>✦ Best fit for your goal</span>
            </div>
          </div>
          <QuestCardRow
            quest={best}
            milestoneCount={milestoneCountByQuest[best.questId] ?? 0}
            onClick={() => onDrillIn(best.questId)}
          />
        </div>
      )}

      {categories.map((cat) => {
        const qs = (questsByCategory[cat.categoryId] ?? [])
          .filter((q) => q.recommendationTier !== "best_fit");
        if (qs.length === 0) return null;
        const accent = categoryAccentColor(cat.accentColor);
        return (
          <div
            key={cat.categoryId}
            className={categoryWrapStyle}
            data-testid={`explore-category-${cat.categoryId}`}
          >
            <div
              className={categoryHeaderStyle}
              style={{
                background: "var(--color-bg-subtle)",
                color: accent,
              }}
            >
              <div className={categoryHeaderTitleStyle}>
                <span>● {cat.categoryName}</span>
              </div>
              <div className={categoryHeaderCountStyle}>
                {qs.length} {qs.length === 1 ? "quest" : "quests"}
              </div>
            </div>
            {qs.map((q) => (
              <QuestCardRow
                key={q.questId}
                quest={q}
                milestoneCount={milestoneCountByQuest[q.questId] ?? 0}
                onClick={() => onDrillIn(q.questId)}
              />
            ))}
          </div>
        );
      })}

      <button
        type="button"
        className={fullMapLinkStyle}
        onClick={switchToMap}
        data-testid="explore-quests-see-map"
      >
        🗺 See full domain map & landmarks →
      </button>
    </>
  );
};

const QuestCardRow: React.FC<{
  quest: ArcQuestRecommendation;
  milestoneCount: number;
  onClick: () => void;
}> = ({ quest, milestoneCount, onClick }) => {
  const concepts = parseChips(quest.coreConcepts).slice(0, 3);
  const tier = tierDisplay(quest.recommendationTier);
  const desc = (quest.whyItMatters || "").split(/(?<=[.!?])\s/)[0]
    .replace(/\*\*([^*]+)\*\*/g, "$1");

  return (
    <div
      className={questCardStyle}
      onClick={onClick}
      data-testid={`explore-quest-${quest.questId}`}
      role="button"
    >
      <span
        className={questIconStyle}
        aria-hidden="true"
        dangerouslySetInnerHTML={{ __html: questIconSvg(quest.iconKey) }}
      />
      <div>
        <div className={questTitleStyle}>{quest.title}</div>
        <div className={questDescStyle}>{desc}</div>
        <div className={buildsTowardLineStyle}>
          <span className="label">Builds toward:</span>
          {quest.whatItUnlocks.replace(/^After this[,.]?\s*/i, "")
            .replace(/\.$/, "")}
        </div>
        <div className={chipRowStyle}>
          {concepts.map((c) => (
            <span key={c} className={chipStyle}>{c}</span>
          ))}
          <span
            className={tierTagStyle}
            style={{ background: tier.bg, color: tier.text }}
          >
            {tier.label}
          </span>
        </div>
        {milestoneCount > 0 && (
          <div
            className={buildsTowardLineStyle}
            style={{ marginTop: 6 }}
          >
            {milestoneCount} milestones
          </div>
        )}
      </div>
    </div>
  );
};
